import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Edit, Trash2, X, Loader2, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ASSET_BASE = process.env.PUBLIC_URL || '';
const resolveImg = (src) => {
  if (!src) return '';
  if (typeof src !== 'string') return '';
  if (src.startsWith('http') || src.startsWith('data:') || src.startsWith('blob:')) return src;
  // kalau backend kasih "/uploads/..." atau "/media/..."
  if (src.startsWith('/')) return `${ASSET_BASE}${src}`;
  return src;
};

const formatDateTime = (d) => {
  try {
    return new Date(d).toLocaleString();
  } catch {
    return '';
  }
};

const ConfirmModal = ({
  open,
  title = 'Confirm',
  description = 'Are you sure?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  loading = false,
  destructive = false,
  onConfirm,
  onClose,
}) => {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-card w-full max-w-md mx-4 rounded-xl border border-border shadow-lg p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-bold text-foreground">{title}</h3>
            <p className="text-sm text-muted-foreground mt-1">{description}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted"
            aria-label="Close"
            disabled={loading}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex gap-3 mt-5">
          <Button
            variant="outline"
            className="flex-1"
            onClick={onClose}
            disabled={loading}
          >
            {cancelText}
          </Button>
          <Button
            className={`flex-1 ${destructive ? 'bg-red-600 hover:bg-red-700' : ''}`}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Processing...
              </span>
            ) : (
              confirmText
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

const MyPostsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Edit modal
  const [editingPost, setEditingPost] = useState(null);
  const [editText, setEditText] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Remove media confirmation
  const [removeMediaTarget, setRemoveMediaTarget] = useState(null); // { postId, mediaIndex }
  const [removingMedia, setRemovingMedia] = useState(false);

  const sortedPosts = useMemo(() => {
    const arr = Array.isArray(posts) ? [...posts] : [];
    // newest first
    arr.sort((a, b) => {
      const da = new Date(a?.created_at || 0).getTime();
      const db = new Date(b?.created_at || 0).getTime();
      return db - da;
    });
    return arr;
  }, [posts]);

  const fetchMyPosts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/me/posts?limit=100`);
      setPosts(response.data.posts || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMyPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEdit = (post) => {
    setEditingPost(post);
    setEditText(post?.text || '');
  };

  const handleSaveEdit = async () => {
    if (!editingPost) return;

    const newText = editText.trim();
    if (!newText) {
      toast.error('Post text cannot be empty');
      return;
    }

    setSavingEdit(true);
    try {
      await axios.patch(`${API}/me/posts/${editingPost.id}`, { text: newText });
      toast.success('Post updated');

      // Optimistic update (biar terasa cepat)
      setPosts((prev) =>
        prev.map((p) => (p.id === editingPost.id ? { ...p, text: newText } : p))
      );

      setEditingPost(null);
      setEditText('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update post');
    } finally {
      setSavingEdit(false);
    }
  };

  const askDelete = (postId) => {
    setDeleteTarget(postId);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/me/posts/${deleteTarget}`);
      toast.success('Post deleted');

      // Optimistic remove
      setPosts((prev) => prev.filter((p) => p.id !== deleteTarget));
      setDeleteTarget(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete post');
    } finally {
      setDeleting(false);
    }
  };

  const askRemoveMedia = (postId, mediaIndex) => {
    setRemoveMediaTarget({ postId, mediaIndex });
  };

  const confirmRemoveMedia = async () => {
    if (!removeMediaTarget) return;
    setRemovingMedia(true);
    const { postId, mediaIndex } = removeMediaTarget;

    try {
      // backend kamu pakai query param remove_media_index
      await axios.patch(`${API}/me/posts/${postId}?remove_media_index=${mediaIndex}`, {});
      toast.success('Media removed');

      // Refresh paling aman (karena urutan array bisa berubah)
      await fetchMyPosts();
      setRemoveMediaTarget(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove media');
    } finally {
      setRemovingMedia(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="text-lg font-semibold">Please login</div>
          <Button className="mt-3" onClick={() => navigate('/login')}>
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Full width feel, tapi tetap enak dibaca */}
      <div className="w-full px-4 sm:px-6 lg:px-10 py-8">
        <div className="flex items-center justify-between gap-3 mb-6">
          <div>
            <Button variant="ghost" onClick={() => navigate('/')} className="-ml-2">
              ← Back to Home
            </Button>
            <h1 className="text-3xl font-bold text-foreground mt-2">My Posts</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Edit, delete, and manage your post media.
            </p>
          </div>

          <Button
            variant="outline"
            onClick={fetchMyPosts}
            disabled={loading}
            className="gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-20 text-muted-foreground">Loading...</div>
        ) : sortedPosts.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">No posts yet</div>
        ) : (
          <div className="space-y-4 max-w-4xl">
            {sortedPosts.map((post) => (
              <Card key={post.id}>
                <CardContent className="p-4 sm:p-5">
                  <div className="flex justify-between items-start gap-3 mb-3">
                    <div className="flex items-center gap-2">
                      <Badge className="capitalize">{post.post_type || 'POST'}</Badge>
                      {post.is_pinned && (
                        <Badge variant="outline">Pinned</Badge>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(post)}
                        disabled={deleting || removingMedia || savingEdit}
                        className="gap-2"
                      >
                        <Edit className="w-4 h-4" />
                        <span className="hidden sm:inline">Edit</span>
                      </Button>

                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => askDelete(post.id)}
                        disabled={deleting || removingMedia || savingEdit}
                        className="gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span className="hidden sm:inline">Delete</span>
                      </Button>
                    </div>
                  </div>

                  <p className="whitespace-pre-wrap text-foreground mb-3">
                    {post.text}
                  </p>

                  {Array.isArray(post.media_images) && post.media_images.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {post.media_images.map((img, idx) => (
                        <div key={idx} className="relative group">
                          <img
                            src={resolveImg(img)}
                            className="w-full h-28 sm:h-24 object-cover rounded-lg border border-border"
                            alt=""
                            loading="lazy"
                            onError={(e) => {
                              e.currentTarget.src = `${ASSET_BASE}/demo/placeholder.jpg`;
                            }}
                          />
                          <button
                            onClick={() => askRemoveMedia(post.id, idx)}
                            className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                            title="Remove media"
                            aria-label="Remove media"
                            disabled={removingMedia || deleting || savingEdit}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-between mt-3">
                    <p className="text-xs text-muted-foreground">
                      {formatDateTime(post.created_at)}
                    </p>

                    {/* kalau backend kamu punya counts, ini auto kebaca */}
                    <div className="text-xs text-muted-foreground flex gap-3">
                      {typeof post.like_count === 'number' && (
                        <span>{post.like_count} likes</span>
                      )}
                      {typeof post.comment_count === 'number' && (
                        <span>{post.comment_count} comments</span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Edit Modal */}
        {editingPost && (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            onClick={() => (savingEdit ? null : setEditingPost(null))}
          >
            <div
              className="bg-card rounded-xl p-5 max-w-lg w-full mx-4 border border-border shadow-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <h3 className="text-lg font-bold text-foreground">Edit Post</h3>
                  <p className="text-sm text-muted-foreground">
                    Update your post content.
                  </p>
                </div>
                <button
                  onClick={() => (savingEdit ? null : setEditingPost(null))}
                  className="p-2 rounded-lg hover:bg-muted"
                  aria-label="Close"
                  disabled={savingEdit}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <Textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                rows={7}
                className="mb-4"
                disabled={savingEdit}
              />

              <div className="flex gap-3">
                <Button
                  onClick={() => setEditingPost(null)}
                  variant="outline"
                  className="flex-1"
                  disabled={savingEdit}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveEdit}
                  className="flex-1"
                  disabled={savingEdit}
                >
                  {savingEdit ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </span>
                  ) : (
                    'Save'
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Confirm delete */}
        <ConfirmModal
          open={!!deleteTarget}
          title="Delete post?"
          description="This will permanently remove your post. You can’t undo this."
          confirmText="Delete"
          cancelText="Cancel"
          destructive
          loading={deleting}
          onClose={() => (deleting ? null : setDeleteTarget(null))}
          onConfirm={confirmDelete}
        />

        {/* Confirm remove media */}
        <ConfirmModal
          open={!!removeMediaTarget}
          title="Remove media?"
          description="This will remove the selected image from your post."
          confirmText="Remove"
          cancelText="Cancel"
          destructive
          loading={removingMedia}
          onClose={() => (removingMedia ? null : setRemoveMediaTarget(null))}
          onConfirm={confirmRemoveMedia}
        />
      </div>
    </div>
  );
};

export default MyPostsPage;
